/**
 * Phase 36a — Axis Bank CIT portal (Wave 1).
 * CIT vendor registry and management.
 */
import { useState, useEffect } from 'react';
import { opsApi } from '@/services/api';
import { useTrackOticAuth } from '@/context/TrackOticAuthContext';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { toast } from 'sonner';
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from '@/components/ui/table';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription,
} from '@/components/ui/dialog';
import {
  Briefcase, Plus, Search, Loader2, Edit2, RefreshCw, CheckCircle2,
} from 'lucide-react';

const BILLING_OPTIONS = [
  { value: 'per_km',                 label: 'Per km' },
  { value: 'per_trip',               label: 'Per trip' },
  { value: 'fixed_monthly_plus_km',  label: 'Fixed monthly + km' },
];

const BLANK = {
  name: '', gstin: '', pan: '', contract_number: '', contract_valid_till: '',
  billing_basis: 'per_km', regions: '', contact_person: '', contact_phone: '', contact_email: '',
};

export default function OpsCitVendorsPage() {
  const { tenant } = useTrackOticAuth();
  const [loading, setLoading]       = useState(true);
  const [saving, setSaving]         = useState(false);
  const [vendors, setVendors]       = useState([]);
  const [searchQuery, setSearchQ]   = useState('');
  const [statusFilter, setStatus]   = useState('');
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editing, setEditing]       = useState(null);
  const [form, setForm]             = useState(BLANK);

  const fetchVendors = async () => {
    setLoading(true);
    try {
      const res = await opsApi.getCitVendors(statusFilter ? { status: statusFilter } : {});
      setVendors(res.data || []);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to load CIT vendors');
      setVendors([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { fetchVendors(); /* eslint-disable-next-line */ }, [statusFilter]);

  const openCreate = () => { setEditing(null); setForm(BLANK); setDialogOpen(true); };
  const openEdit   = (v) => {
    setEditing(v);
    setForm({
      name: v.name || '',
      gstin: v.gstin || '',
      pan: v.pan || '',
      contract_number: v.contract_number || '',
      contract_valid_till: v.contract_valid_till || '',
      billing_basis: v.billing_basis || 'per_km',
      regions: (v.regions || []).join(', '),
      contact_person: v.contact_person || '',
      contact_phone: v.contact_phone || '',
      contact_email: v.contact_email || '',
    });
    setDialogOpen(true);
  };

  const handleSave = async () => {
    if (!form.name.trim()) { toast.error('Vendor name is required'); return; }
    const payload = {
      name:            form.name,
      gstin:           form.gstin || null,
      pan:             form.pan || null,
      contract_number: form.contract_number || null,
      contract_valid_till: form.contract_valid_till || null,
      billing_basis:   form.billing_basis,
      regions:         form.regions.split(',').map((r) => r.trim()).filter(Boolean),
      contact_person:  form.contact_person || null,
      contact_phone:   form.contact_phone || null,
      contact_email:   form.contact_email || null,
    };
    setSaving(true);
    try {
      if (editing) {
        await opsApi.updateCitVendor(editing.id, payload);
        toast.success('Vendor updated');
      } else {
        await opsApi.createCitVendor(payload);
        toast.success('Vendor registered (pending approval)');
      }
      setDialogOpen(false);
      fetchVendors();
    } catch (err) {
      toast.error(err.response?.data?.detail || err.response?.data?.message || 'Save failed');
    } finally {
      setSaving(false);
    }
  };

  const handleApprove = async (v) => {
    if (!window.confirm(`Approve "${v.name}" and set status to Active?`)) return;
    try {
      await opsApi.approveCitVendor(v.id);
      toast.success('Vendor approved');
      fetchVendors();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Approval failed');
    }
  };

  const filtered = vendors.filter(
    (v) => v.name?.toLowerCase().includes(searchQuery.toLowerCase())
        || v.gstin?.toLowerCase().includes(searchQuery.toLowerCase()),
  );

  const activeCount  = vendors.filter((v) => v.status === 'active').length;
  const pendingCount = vendors.filter((v) => v.status === 'pending_approval').length;

  return (
    <div className="p-8" data-testid="ops-cit-vendors-page">
      <div className="flex items-center justify-between mb-8">
        <div>
          <h1 className="text-3xl font-bold text-white flex items-center gap-3">
            <Briefcase className="w-8 h-8 text-teal-500" />
            CIT Vendors
          </h1>
          <p className="text-slate-400 mt-1">Third-party vendors operating vans for {tenant?.name}</p>
        </div>
        <div className="flex gap-3">
          <Button onClick={fetchVendors} variant="outline" className="border-slate-700 text-slate-300">
            <RefreshCw className="w-4 h-4 mr-2" /> Refresh
          </Button>
          <Button onClick={openCreate} className="bg-teal-600 hover:bg-teal-700" data-testid="ops-add-vendor-btn">
            <Plus className="w-4 h-4 mr-2" /> Register vendor
          </Button>
        </div>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-8">
        <StatCard icon={Briefcase} label="Total" value={vendors.length} />
        <StatCard icon={CheckCircle2} label="Active" value={activeCount} valueClass="text-green-400" />
        <StatCard icon={CheckCircle2} label="Pending approval" value={pendingCount} valueClass="text-amber-400" />
        <StatCard icon={CheckCircle2} label="Vans assigned" value={vendors.reduce((s, v) => s + (v.vans_count || 0), 0)} valueClass="text-blue-400" />
      </div>

      <div className="flex gap-3 mb-6">
        <div className="relative max-w-md flex-1">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-500 w-4 h-4" />
          <Input
            placeholder="Search by name or GSTIN..."
            value={searchQuery}
            onChange={(e) => setSearchQ(e.target.value)}
            className="pl-10 bg-slate-900 border-slate-800 text-white"
          />
        </div>
        <select
          value={statusFilter}
          onChange={(e) => setStatus(e.target.value)}
          className="rounded-md border border-slate-800 bg-slate-900 text-white px-3 py-2"
        >
          <option value="">All statuses</option>
          <option value="active">Active</option>
          <option value="pending_approval">Pending approval</option>
          <option value="suspended">Suspended</option>
        </select>
      </div>

      <div className="bg-slate-900 rounded-xl border border-slate-800 overflow-hidden">
        <Table>
          <TableHeader>
            <TableRow className="border-slate-800">
              <TableHead className="text-slate-400">Vendor</TableHead>
              <TableHead className="text-slate-400">Contract</TableHead>
              <TableHead className="text-slate-400">Regions</TableHead>
              <TableHead className="text-slate-400">Vans</TableHead>
              <TableHead className="text-slate-400">Drivers</TableHead>
              <TableHead className="text-slate-400">Billing</TableHead>
              <TableHead className="text-slate-400">Status</TableHead>
              <TableHead className="text-slate-400 text-right">Actions</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {loading ? (
              <TableRow><TableCell colSpan={8} className="text-center py-12">
                <Loader2 className="w-8 h-8 animate-spin text-teal-500 mx-auto" />
              </TableCell></TableRow>
            ) : filtered.length === 0 ? (
              <TableRow><TableCell colSpan={8} className="text-center py-12 text-slate-500">
                No CIT vendors found
              </TableCell></TableRow>
            ) : (
              filtered.map((v) => (
                <TableRow key={v.id} className="border-slate-800" data-testid={`ops-vendor-row-${v.id}`}>
                  <TableCell>
                    <div>
                      <p className="text-white font-medium">{v.name}</p>
                      <p className="text-slate-500 text-sm">GSTIN: {v.gstin || '—'} · PAN: {v.pan || '—'}</p>
                    </div>
                  </TableCell>
                  <TableCell className="text-slate-300 text-sm">
                    <p>{v.contract_number || '—'}</p>
                    <p className="text-slate-500">till {v.contract_valid_till || '—'}</p>
                  </TableCell>
                  <TableCell className="text-slate-300 text-sm">
                    {(v.regions || []).join(', ') || '—'}
                  </TableCell>
                  <TableCell className="text-slate-200">{v.vans_count ?? 0}</TableCell>
                  <TableCell className="text-slate-200">{v.drivers_count ?? 0}</TableCell>
                  <TableCell className="text-slate-300 text-sm">
                    {BILLING_OPTIONS.find((b) => b.value === v.billing_basis)?.label || v.billing_basis}
                  </TableCell>
                  <TableCell>
                    <Badge className={statusBadge(v.status)}>{statusLabel(v.status)}</Badge>
                  </TableCell>
                  <TableCell className="text-right">
                    <div className="flex justify-end gap-1">
                      {v.status === 'pending_approval' && (
                        <Button
                          variant="ghost"
                          size="icon"
                          onClick={() => handleApprove(v)}
                          className="h-8 w-8 text-slate-400 hover:text-green-400"
                          data-testid={`approve-vendor-${v.id}`}
                        >
                          <CheckCircle2 className="w-4 h-4" />
                        </Button>
                      )}
                      <Button
                        variant="ghost"
                        size="icon"
                        onClick={() => openEdit(v)}
                        className="h-8 w-8 text-slate-400 hover:text-teal-400"
                        data-testid={`edit-vendor-${v.id}`}
                      >
                        <Edit2 className="w-4 h-4" />
                      </Button>
                    </div>
                  </TableCell>
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
      </div>

      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent className="bg-slate-900 border-slate-800 max-w-xl">
          <DialogHeader>
            <DialogTitle className="text-white">
              {editing ? 'Edit CIT Vendor' : 'Register CIT Vendor'}
            </DialogTitle>
            <DialogDescription className="text-slate-400">
              New vendors are created in pending approval until an Axis admin activates them.
            </DialogDescription>
          </DialogHeader>
          <div className="grid grid-cols-2 gap-4 mt-2">
            <FormField label="Company name" value={form.name} onChange={(v) => setForm({ ...form, name: v })} span2 />
            <FormField label="GSTIN" value={form.gstin} onChange={(v) => setForm({ ...form, gstin: v })} />
            <FormField label="PAN" value={form.pan} onChange={(v) => setForm({ ...form, pan: v })} />
            <FormField label="Contract number" value={form.contract_number} onChange={(v) => setForm({ ...form, contract_number: v })} />
            <FormField label="Contract valid till" type="date" value={form.contract_valid_till} onChange={(v) => setForm({ ...form, contract_valid_till: v })} />
            <FormField label="Regions (comma-separated)" value={form.regions} onChange={(v) => setForm({ ...form, regions: v })} span2 placeholder="Maharashtra, Delhi NCR, Karnataka" />
            <div>
              <Label className="text-slate-300">Billing basis</Label>
              <select
                value={form.billing_basis}
                onChange={(e) => setForm({ ...form, billing_basis: e.target.value })}
                className="w-full mt-1 rounded-md border border-slate-800 bg-slate-900 text-white px-3 py-2"
              >
                {BILLING_OPTIONS.map((b) => <option key={b.value} value={b.value}>{b.label}</option>)}
              </select>
            </div>
            <FormField label="Contact person" value={form.contact_person} onChange={(v) => setForm({ ...form, contact_person: v })} />
            <FormField label="Contact phone" value={form.contact_phone} onChange={(v) => setForm({ ...form, contact_phone: v })} />
            <FormField label="Contact email" type="email" value={form.contact_email} onChange={(v) => setForm({ ...form, contact_email: v })} />
          </div>
          <div className="flex justify-end gap-2 mt-6">
            <Button variant="outline" className="border-slate-700 text-slate-300" onClick={() => setDialogOpen(false)}>Cancel</Button>
            <Button className="bg-teal-600 hover:bg-teal-700" onClick={handleSave} disabled={saving} data-testid="save-vendor-btn">
              {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : (editing ? 'Save changes' : 'Register')}
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}

function statusBadge(s) {
  switch (s) {
    case 'active':           return 'bg-green-500/20 text-green-400 border-green-500/30';
    case 'pending_approval': return 'bg-amber-500/20 text-amber-400 border-amber-500/30';
    case 'suspended':        return 'bg-red-500/20 text-red-400 border-red-500/30';
    default:                 return 'bg-slate-500/20 text-slate-400 border-slate-500/30';
  }
}
function statusLabel(s) { return s === 'pending_approval' ? 'Pending' : (s || '—'); }

function StatCard({ icon: Icon, label, value, valueClass = 'text-white' }) {
  return (
    <div className="bg-slate-900 border border-slate-800 rounded-xl p-4 flex items-center gap-3">
      <div className="w-10 h-10 rounded-lg bg-slate-800 flex items-center justify-center">
        <Icon className="w-5 h-5 text-teal-400" />
      </div>
      <div>
        <p className="text-slate-500 text-xs uppercase">{label}</p>
        <p className={`text-xl font-bold ${valueClass}`}>{value}</p>
      </div>
    </div>
  );
}

function FormField({ label, value, onChange, type = 'text', span2 = false, placeholder = '' }) {
  return (
    <div className={span2 ? 'col-span-2' : ''}>
      <Label className="text-slate-300">{label}</Label>
      <Input
        type={type}
        value={value}
        onChange={(e) => onChange(e.target.value)}
        placeholder={placeholder}
        className="mt-1 bg-slate-900 border-slate-800 text-white"
      />
    </div>
  );
}
